import { Box, Paper, Typography, Stack, alpha, useTheme } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { MdOutlineRadioButtonUnchecked } from "react-icons/md";
import { useAuth } from "../../authentication/AuthProvider";
import AddButton from "../../components/button/AddButton";
import RefreshButton from "../../components/button/RefreshButton";
import ResizableBox from "../../components/container/ResizableBox";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ContextMenu from "../../components/context-menu/ContextMenu";
import DialogDelete from "../../components/dialog/DialogDelete";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, leftAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import { User } from "../../db_access/user";

const ManageColleagues = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [searchText, setSearchText] = useState("");
  const gridRef = useRef(null);
  const dialogRef = useRef(null);
  const getColleagues = () => {
    User.get_my_colleagues()
      .then((response) => response.json())
      .then((data) =>
        setUsers(
          data.map((d) => ({
            ...d,
            is_active: d.is_active === 1,
            is_dpp_enabled: d.is_dpp_enabled === 1,
            is_collaborate_internal_enabled: d.is_collaborate_internal_enabled === 1,
            is_collaborate_external_enabled: d.is_collaborate_external_enabled === 1,
            is_product_enabled: d.is_product_enabled === 1,
            is_project_enabled: d.is_project_enabled === 1,
            is_building_enabled: d.is_building_enabled === 1,
            is_organization_enabled: d.is_organization_enabled === 1,
            is_company_admin: d.is_company_admin === 1,
          }))
        )
      );
  };
  const deleteUsers = (selectedUsers) => {
    User.delete_colleagues(selectedUsers.map((s) => s.id)).finally(() => {
      setSelectedUsers([]);
      setRefresh((r) => !r);
    });
  };
  const newUser = () => {
    User.new_colleague().finally(() => setRefresh((r) => !r));
  };
  const updateUser = (user) => {
    User.update_colleague(user).finally(() => { });
  };
  const updatePassword = (id, password) => {
    User.change_colleague_password(id, password).finally(() => { });
  };
  const CheckboxEditor = forwardRef((props, ref) => {
    const [checked, setChecked] = useState(!!props.value);

    useEffect(() => {
      setChecked(!!props.value);
    }, [props.value]);

    useImperativeHandle(ref, () => ({
      getValue: () => (checked ? 1 : 0),
    }));
    const handleChange = (event) => {
      const checked = event.target.checked;
      const value = checked ? 1 : 0;
      const updatedRow = {
        ...props.data,
        [props.colDef.field]: value,
      };
      updateUser(updatedRow);
      setUsers((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
    };
    return <input type="checkbox" checked={props.value} onChange={handleChange} />;
  });
  const onCellValueChanged = (params) => {
    const { data, colDef, newValue } = params;
    if (colDef.field === "password") {
      updatePassword(data.id, newValue);
    } else {
      const updatedRow = data;
      updateUser(updatedRow);
      setUsers((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
    }
  };
  const cellRenderer = (params) => {
    return params.value ? <IoMdCheckmarkCircleOutline size={20} color="green" /> : <MdOutlineRadioButtonUnchecked size={20} color="grey" />;
  };
  const onCellClicked = (params) => {
    const { colDef } = params;
    const updatedRow = { ...params.data, [colDef.field]: !params.value };
    console.log(colDef, updatedRow);
    updateUser(updatedRow);
    setUsers((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
  };
  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedUsers(selected);
  };
  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuPosition({ x: e.pageX, y: e.pageY });
    setMenuVisible(true);
  };
  const handleCloseMenu = () => {
    setMenuVisible(false);
  };
  const openDialog = () => {
    if (dialogRef) {
      dialogRef.current.open();
    }
  };
  const checkBoxColDef = {
    ...defaultColDef,
    ...centerAlignColDef,
    cellRenderer,
    onCellClicked,
  };
  const colDefs = [
    { field: "id", hide: true },
    { field: "company_id", hide: true },
    { headerName: "User ID", field: "user_id", ...defaultColDef, ...centerAlignColDef, editable: true, onCellValueChanged },
    { headerName: "Email", field: "email", ...defaultColDef, ...leftAlignColDef, editable: true, onCellValueChanged },
    { headerName: "Phone Number", field: "phone_number", ...defaultColDef, ...leftAlignColDef, editable: true, onCellValueChanged },
    { headerName: "Active", field: "is_active", ...checkBoxColDef, editable: true },
    { headerName: "CAdmin", field: "is_company_admin", ...checkBoxColDef, editable: true },
    { headerName: "DPP", field: "is_dpp_enabled", ...checkBoxColDef, editable: true, hide: user["is_dpp_enabled"] === 0 },
    { headerName: "Collaborate Internal", field: "is_collaborate_internal_enabled", ...checkBoxColDef, editable: true, hide: user["is_collaborate_internal_enabled"] === 0 },
    { headerName: "Collaborate External", field: "is_collaborate_external_enabled", ...checkBoxColDef, editable: true, hide: user["is_collaborate_external_enabled"] === 0 },
    { headerName: "Product", field: "is_product_enabled", ...checkBoxColDef, editable: true, hide: user["is_product_enabled"] === 0 },
    { headerName: "Project", field: "is_project_enabled", ...checkBoxColDef, editable: true, hide: user["is_project_enabled"] === 0 },
    { headerName: "Building", field: "is_building_enabled", ...checkBoxColDef, editable: true, hide: user["is_building_enabled"] === 0 },
    { headerName: "Organization", field: "is_organization_enabled", ...checkBoxColDef, editable: true, hide: user["is_organization_enabled"] === 0 },
    { headerName: "Password", field: "password", ...defaultColDef, ...centerAlignColDef, editable: true, valueFormatter: () => "*****" },
  ];
  const contextMenuOptions = [{ label: "Delete", action: openDialog, needSelectedItems: true }];

  useEffect(() => getColleagues(), [refresh]);
  useEffect(() => console.log(users), [users]);
  useEffect(() => console.log(menuPosition), [menuPosition]);
  useEffect(() => console.log(selectedUsers), [selectedUsers]);
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);
  return (
    isAuthorized && (
      <Stack spacing={3} sx={{ bgcolor: 'transparent', border: 'none', boxShadow: 'none' }}>
        <TableHeaderContainer sx={{ bgcolor: 'transparent', border: 'none', boxShadow: 'none', px: 0 }}>
          <TableTitleContainer>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 1000, color: 'text.primary', letterSpacing: '-1.5px', mb: 0.5 }}>
                {t("Manage Users")}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Manage team members and their individual module permissions
              </Typography>
            </Box>
          </TableTitleContainer>
          <TableHeaderToolsContainer>
            <SearchBox text={searchText} setText={setSearchText} sx={{ bgcolor: 'transparent' }} />
            <AddButton label="New User" onClick={newUser} sx={{ borderRadius: 3, fontWeight: 900 }} />
            <ExportAsXLSXButton data={users} label="" filename="users.xlsx" sx={{ width: 52, height: 52, p: 0, borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1), bgcolor: 'transparent', boxShadow: 'none' }} />
            <RefreshButton setRefresh={setRefresh} label="" sx={{ width: 52, height: 52, p: 0, borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.divider, 0.1), bgcolor: 'transparent', boxShadow: 'none' }} />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box sx={{ bgcolor: 'transparent', border: 'none' }}>
          <ResizableBox>
            <AgGrid rowData={users} columnDefs={colDefs} handleContextMenu={handleContextMenu} onSelectionChanged={onSelectionChanged} />
          </ResizableBox>
        </Box>
        <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={selectedUsers} options={contextMenuOptions} />
        <DialogDelete selectedItems={selectedUsers} handleDelete={deleteUsers} ref={dialogRef} label={"user_id"} />
      </Stack>
    )
  );
};
export default ManageColleagues;
